import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, ClipboardList, Search, Clapperboard, FileText, X, Calendar, CalendarDays, Tag, Eye, MessageSquare } from 'lucide-react';
import api from '../services/api';
import logger from '../utils/logger';

const MyApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const res = await api.get('/applications/my');
      setApplications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage et tri
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];

    // Recherche
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(app =>
        (app.task_titre || '').toLowerCase().includes(searchLower) ||
        (app.project_titre || '').toLowerCase().includes(searchLower) ||
        (app.motivation || '').toLowerCase().includes(searchLower)
      );
    }

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.statut === statusFilter);
    }

    // Tri
    filtered.sort((a, b) => {
      const dateA = new Date(a.date_candidature || a.created_at);
      const dateB = new Date(b.date_candidature || b.created_at);
      
      switch (sortBy) {
        case 'recent':
          return dateB - dateA;
        case 'oldest':
          return dateA - dateB;
        case 'status':
          const statusOrder = { 'acceptee': 0, 'en_attente': 1, 'refusee': 2 };
          return (statusOrder[a.statut] || 3) - (statusOrder[b.statut] || 3);
        default:
          return dateB - dateA;
      }
    });

    return filtered;
  }, [applications, search, statusFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, sortBy]);

  // Stats
  const stats = useMemo(() => ({
    total: applications.length,
    pending: applications.filter(a => a.statut === 'en_attente').length,
    accepted: applications.filter(a => a.statut === 'acceptee').length,
    rejected: applications.filter(a => a.statut === 'refusee').length
  }), [applications]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_attente': return 'bg-yellow-100 text-yellow-700';
      case 'acceptee': return 'bg-green-100 text-green-700';
      case 'refusee': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'en_attente': return 'En attente';
      case 'acceptee': return 'Acceptée';
      case 'refusee': return 'Refusée';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'en_attente': return <Clock size={16} />;
      case 'acceptee': return <CheckCircle size={16} />;
      case 'refusee': return <XCircle size={16} />;
      default: return <ClipboardList size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-indigo-50 to-blue-50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
            <span className="absolute inset-0 flex items-center justify-center"><ClipboardList size={24} /></span>
          </div>
          <p className="text-indigo-600 font-medium mt-4">Chargement de vos candidatures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-indigo-50 to-blue-50 pb-10">
      {/* Header Hero */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6bTAtMzBoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-5xl mx-auto px-4 py-8 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition text-sm">
            ← Retour à l'accueil
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <span className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center"><ClipboardList size={24} /></span>
                Mes candidatures
              </h1>
              <p className="text-white/80 mt-2">Suivez l'évolution de vos candidatures aux projets</p>
            </div>
            
            <Link 
              to="/"
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur hover:bg-white/30 px-4 py-2 rounded-xl transition text-sm font-medium"
            >
              <Search size={16} /> Trouver des projets
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center border border-white/20">
              <div className="text-3xl font-bold">{stats.total}</div>
              <div className="text-sm text-white/80">Total</div>
            </div>
            <div className="bg-yellow-500/20 backdrop-blur rounded-xl p-4 text-center border border-yellow-300/30">
              <div className="text-3xl font-bold text-yellow-200">{stats.pending}</div>
              <div className="text-sm text-yellow-100/80 flex items-center justify-center gap-1">
                <Clock size={14} /> En attente
              </div>
            </div>
            <div className="bg-green-500/20 backdrop-blur rounded-xl p-4 text-center border border-green-300/30">
              <div className="text-3xl font-bold text-green-200">{stats.accepted}</div>
              <div className="text-sm text-green-100/80 flex items-center justify-center gap-1">
                <CheckCircle size={14} /> Acceptées
              </div>
            </div>
            <div className="bg-red-500/20 backdrop-blur rounded-xl p-4 text-center border border-red-300/30">
              <div className="text-3xl font-bold text-red-200">{stats.rejected}</div>
              <div className="text-sm text-red-100/80 flex items-center justify-center gap-1">
                <XCircle size={14} /> Refusées
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-4">
        {applications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={40} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucune candidature</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Vous n'avez pas encore postulé à des tâches. Parcourez les projets disponibles pour trouver des opportunités !
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium rounded-xl hover:shadow-lg hover:scale-105 transition-all"
            >
              <Clapperboard size={16} /> Parcourir les projets
            </Link>
          </div>
        ) : (
          <>
            {/* Barre de recherche sticky */}
            <div className="sticky top-16 z-30 py-2">
              <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg p-3 border border-indigo-100">
                <div className="flex gap-2">
                  {/* Recherche */}
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2"><Search size={18} /></span>
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all"
                    />
                    {search && (
                      <button 
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Bouton filtres */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-medium transition-all ${
                      showFilters || statusFilter !== 'all'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span className="hidden sm:inline">Filtres</span>
                    {statusFilter !== 'all' && (
                      <span className="bg-white text-indigo-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">1</span>
                    )}
                  </button>

                  {/* Tri - caché sur mobile */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="hidden sm:block px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  >
                    <option value="recent">Récentes</option>
                    <option value="oldest">Anciennes</option>
                    <option value="status">Par statut</option>
                  </select>
                </div>

                {/* Panneau de filtres dépliable */}
                {showFilters && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                    {/* Filtres par statut */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1.5 block">Statut</label>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => setStatusFilter('all')}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            statusFilter === 'all'
                              ? 'bg-indigo-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          Toutes ({stats.total})
                        </button>
                        <button
                          onClick={() => setStatusFilter('en_attente')}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            statusFilter === 'en_attente'
                              ? 'bg-yellow-500 text-white'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                        >
                          <Clock size={14} className="inline" /> En attente ({stats.pending})
                        </button>
                        <button
                          onClick={() => setStatusFilter('acceptee')}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            statusFilter === 'acceptee'
                              ? 'bg-green-500 text-white'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          <CheckCircle size={14} className="inline" /> Acceptées ({stats.accepted})
                        </button>
                        <button
                          onClick={() => setStatusFilter('refusee')}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            statusFilter === 'refusee'
                              ? 'bg-red-500 text-white'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          <XCircle size={14} className="inline" /> Refusées ({stats.rejected})
                        </button>
                      </div>
                    </div>

                    {/* Tri - visible sur mobile */}
                    <div className="sm:hidden">
                      <label className="text-xs font-medium text-gray-500 mb-1.5 block">Tri</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                      >
                        <option value="recent">Plus récentes</option>
                        <option value="oldest">Plus anciennes</option>
                        <option value="status">Par statut</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Badge filtre actif */}
                {!showFilters && statusFilter !== 'all' && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      statusFilter === 'en_attente' ? 'bg-yellow-100 text-yellow-700' :
                      statusFilter === 'acceptee' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {getStatusIcon(statusFilter)} {getStatusLabel(statusFilter)}
                      <button onClick={() => setStatusFilter('all')}><X size={14} /></button>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Compteur de résultats */}
            <div className="flex items-center justify-between mt-4 mb-4">
              <p className="text-gray-600 text-sm">
                <span className="font-bold text-indigo-600 text-lg">{filteredApplications.length}</span> candidature(s)
                {search && <span className="text-gray-400"> pour "{search}"</span>}
              </p>
              {(search || statusFilter !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setStatusFilter('all'); }}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Effacer les filtres
                </button>
              )}
            </div>

            {/* Liste des candidatures */}
            {paginatedApplications.length > 0 ? (
              <div className="space-y-4">
                {paginatedApplications.map((app, index) => (
                  <div 
                    key={app.id} 
                    className={`group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-l-4 ${
                      app.statut === 'acceptee' ? 'border-green-500' :
                      app.statut === 'refusee' ? 'border-red-500' :
                      'border-yellow-500'
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="p-4 md:p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Icône statut */}
                        <div className={`hidden md:flex w-14 h-14 rounded-xl items-center justify-center text-2xl flex-shrink-0 ${
                          app.statut === 'acceptee' ? 'bg-green-100' :
                          app.statut === 'refusee' ? 'bg-red-100' :
                          'bg-yellow-100'
                        }`}>
                          {getStatusIcon(app.statut)}
                        </div>
                        
                        {/* Contenu */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getStatusColor(app.statut)}`}>
                              {getStatusIcon(app.statut)} {getStatusLabel(app.statut)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(app.date_candidature || app.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          
                          <h3 className="text-lg font-bold text-gray-800 mb-1 truncate group-hover:text-indigo-600 transition">
                            {app.task_titre || `Tâche #${app.task_id}`}
                          </h3>
                          
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clapperboard size={14} /> 
                              <span className="truncate max-w-[200px]">{app.project_titre || `Projet #${app.project_id}`}</span>
                            </span>
                            {app.task_type && (
                              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-md text-xs font-medium">
                                {app.task_type}
                              </span>
                            )}
                          </div>
                          
                          {app.motivation && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                              <p className="text-sm text-gray-600 line-clamp-2">
                                <MessageSquare size={14} className="inline font-medium text-gray-700" />{' '}
                                {app.motivation}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex md:flex-col gap-2 md:items-end">
                          <Link
                            to={`/project/${app.project_id}`}
                            className="flex-1 md:flex-none px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition text-sm text-center"
                          >
                            <Eye size={14} /> Voir
                          </Link>
                          {app.statut === 'acceptee' && (
                            <Link
                              to={`/project/${app.project_id}`}
                              className="flex-1 md:flex-none px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:shadow-lg transition text-sm text-center"
                            >
                              <CheckCircle size={14} /> Accéder
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search size={30} />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Aucun résultat</h3>
                <p className="text-gray-500 mb-4">Aucune candidature ne correspond à vos critères</p>
                <button
                  onClick={() => { setSearch(''); setStatusFilter('all'); }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8">
                <div className="flex justify-center items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
                  >
                    ← Préc
                  </button>
                  
                  <div className="flex gap-1 items-center">
                    {(() => {
                      const pages = [];
                      const showPages = new Set();
                      showPages.add(1);
                      showPages.add(totalPages);
                      for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                        showPages.add(i);
                      }
                      const sortedPages = [...showPages].sort((a, b) => a - b);
                      sortedPages.forEach((page, idx) => {
                        if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                          pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                        }
                        pages.push(
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-xl font-medium transition ${currentPage === page ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white shadow-md hover:shadow-lg'}`}
                          >
                            {page}
                          </button>
                        );
                      });
                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
                  >
                    Suiv →
                  </button>
                </div>
                
                <p className="text-center mt-3 text-sm text-gray-500">
                  Page {currentPage} sur {totalPages}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyApplications;
